import type { Flag } from "@unbox/api";
import { AlertCircle, Eye, Plus, PlusCircle, Trash02 } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useAddMissedRisk } from "@/api/assurance/add-missed-risk";
import { useUpdateFlag } from "@/api/assurance/update-flag";
import { useMe } from "@/api/me";
import { useRiskSuggestions } from "@/api/merchant-risks";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { tierToSeverity } from "@/components/safety-config/risk-groups";
import { RiskSelect } from "@/components/safety-config/risk-select";
import { cx } from "@/utils/cx";
import { formatShortDate } from "@/utils/date-conversion";
import { LeaveUnfinishedRiskDialog } from "./LeaveUnfinishedRiskDialog";
import type { RiskEvidence } from "./RiskCard";
import { RiskCard } from "./RiskCard";
import { MonitoringDot, MonitoringDotWithTooltip, RiskMonitoringTip, useRiskMonitoring } from "./RiskMonitoring";
import { useUnfinishedFormGuard } from "./use-unfinished-form-guard";

const OTHER_OPTION_ID = "other";

type AddedRisk = {
  flag: Flag;
  title: string;
  description: string;
};

export type ExistingCustomRisk = {
  id: string;
  title: string;
  description: string;
  severity: "critical" | "high" | "medium" | "low";
  flaggedByName?: string;
  flaggedAt?: string;
};

type CustomRiskRequestProps = {
  videoId: string;
  // Risks already persisted as isCustom (from a prior session or before a refetch
  // landed) — rendered the same way as a just-added one so the look survives a reload.
  existingCustomRisks: ExistingCustomRisk[];
  // The "Add Risk" trigger button lives outside this component (its style differs per
  // surface, e.g. safety-beta vs. the legacy safety page) — this just controls the form.
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRiskRemoved: (flagId: string) => void;
  // "none" drops the border around the form — for callers that already wrap it in
  // their own bordered container (e.g. a dialog).
  border?: "none" | "default";
  // "old" hides the form's own header row (icon, label, cancel button) — the legacy
  // safety page renders its own dialog header and cancel action around this component.
  view?: "old" | "new";
  // Hides the Confirm/Dismiss controls on every risk card this renders — for callers
  // that only display already-confirmed risks.
  readOnly?: boolean;
  /** Tells the caller when a started risk is holding back clicks elsewhere, so it can grey them out. */
  onBlockingChange?: (blocking: boolean) => void;
};

const AddedRiskCard = ({
  id,
  videoId,
  title,
  description,
  severity,
  flaggedByName,
  flaggedAt,
  readOnly,
  onRemoved,
}: {
  id: string;
  videoId: string;
  title: string;
  description: string;
  severity: "critical" | "high" | "medium" | "low";
  flaggedByName?: string;
  flaggedAt?: string;
  readOnly?: boolean;
  onRemoved: (flagId: string) => void;
}) => {
  const updateFlag = useUpdateFlag({ id, videoId } as Flag);
  const { data: me } = useMe();
  const evidence: RiskEvidence[] = [{ timestamp: "", text: description }];

  const onDelete = () =>
    updateFlag.mutate(
      { actionTaken: "flag_dismissed", feedbackReason: "Removed by reviewer" },
      { onSuccess: () => onRemoved(id) },
    );

  return (
    <RiskCard
      {...(readOnly ? { readOnly: true as const } : { readOnly: false as const, onDelete })}
      icon={<Eye className="w-[18px] h-[18px] text-secondary" />}
      title={title}
      severity={severity}
      evidence={evidence}
      typeOfRisk="custom"
      flaggedByName={flaggedByName ?? `${me?.firstName ?? ""} ${me?.lastName ?? ""}`.trim()}
      flaggedAt={flaggedAt ?? formatShortDate(new Date())}
    />
  );
};

export const CustomRiskRequest = ({
  videoId,
  existingCustomRisks,
  open,
  onOpenChange,
  onRiskRemoved,
  border = "default",
  view = "new",
  readOnly = false,
  onBlockingChange,
}: CustomRiskRequestProps) => {
  const [riskKey, setRiskKey] = useState<string | undefined>();
  // Once the reviewer picks a risk themselves, suggestions stop replacing it.
  const [pickedByHand, setPickedByHand] = useState(false);
  const [description, setDescription] = useState("");
  // The risk the reviewer went to switch on in Configure Safety, to confirm it once it's on.
  const [switchingOn, setSwitchingOn] = useState<string | undefined>();
  const [addedRisks, setAddedRisks] = useState<AddedRisk[]>([]);
  // Flag ids added in this session, kept even after the risk migrates from `addedRisks`
  // into `existingCustomRisks` on refetch — otherwise a just-added risk would lose its
  // delete control the moment the video data catches up, despite never having left this
  // session.
  const [sessionAddedIds, setSessionAddedIds] = useState<Set<string>>(new Set());

  const { groups, itemFor, isMonitored } = useRiskMonitoring();
  const addMissedRisk = useAddMissedRisk(videoId);
  const suggestions = useRiskSuggestions(description);

  const formRef = useRef<HTMLDivElement>(null);
  const started = open && (description.trim().length > 0 || !!riskKey);
  const guard = useUnfinishedFormGuard(formRef, view === "new" && started);

  useEffect(() => {
    onBlockingChange?.(guard.nudged);
  }, [guard.nudged, onBlockingChange]);

  const resetForm = () => {
    setRiskKey(undefined);
    setPickedByHand(false);
    setDescription("");
    setSwitchingOn(undefined);
  };

  // Reset when the video actually changes — compared against the previous value
  // rather than a "first render" flag, since React StrictMode double-invokes this
  // effect on mount and would otherwise trigger the reset (and close) anyway. Some
  // callers (e.g. the legacy safety modal) only mount this component once the form
  // is already open, and closing it immediately would defeat that.
  const previousVideoId = useRef(videoId);
  useEffect(() => {
    if (previousVideoId.current === videoId) return;
    previousVideoId.current = videoId;
    setAddedRisks([]);
    setSessionAddedIds(new Set());
    onOpenChange(false);
    resetForm();
  }, [videoId]);

  // Once a just-added risk shows up in existingCustomRisks (the video refetched),
  // drop it from local state so it isn't rendered twice.
  const existingCustomRiskIdsKey = existingCustomRisks.map((r) => r.id).join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: existingCustomRiskIdsKey captures the id set
  useEffect(() => {
    const existingIds = new Set(existingCustomRisks.map((r) => r.id));
    setAddedRisks((prev) => prev.filter((r) => !existingIds.has(r.flag.id)));
  }, [existingCustomRiskIdsKey]);

  useEffect(() => {
    if (pickedByHand) return;
    const suggestedItem = suggestions[0] && itemFor(suggestions[0].riskId);
    setRiskKey(suggestedItem?.key);
  }, [suggestions, pickedByHand]);

  const riskOptionGroups = [
    ...groups,
    { id: OTHER_OPTION_ID, name: "", items: [{ key: OTHER_OPTION_ID, name: "Other" }] },
  ];

  const selectedItem =
    riskKey && riskKey !== OTHER_OPTION_ID
      ? groups.flatMap((group) => group.items).find((item) => item.key === riskKey)
      : undefined;
  const selectedMonitored = selectedItem ? isMonitored(selectedItem.key) : false;
  const justSwitchedOn = !!selectedItem && switchingOn === selectedItem.key && selectedMonitored;

  const canSubmit = !!riskKey && description.trim().length > 0;

  const handleAdd = () => {
    if (!canSubmit) return;

    addMissedRisk.mutate(
      { riskId: riskKey !== OTHER_OPTION_ID ? riskKey : undefined, description: description.trim() },
      {
        onSuccess: (res) => {
          if (!res) return;
          setAddedRisks((prev) => [
            ...prev,
            {
              flag: res.flag as unknown as Flag,
              title: selectedItem?.name ?? "Other",
              description: description.trim(),
            },
          ]);
          setSessionAddedIds((prev) => new Set(prev).add(res.flag.id));
          onOpenChange(false);
          resetForm();
        },
      },
    );
  };

  const handleRemoved = (flagId: string) => {
    setAddedRisks((prev) => prev.filter((r) => r.flag.id !== flagId));
    onRiskRemoved(flagId);
  };

  const discard = () => {
    onOpenChange(false);
    resetForm();
  };

  return (
    // empty:hidden — when there's nothing to show yet (no existing/added risks, form closed),
    // this renders with zero children; collapse it so the parent's gap doesn't add spacing here.
    <div className="flex flex-col gap-3 empty:hidden">
      {existingCustomRisks.map((risk) => (
        <AddedRiskCard
          key={risk.id}
          id={risk.id}
          videoId={videoId}
          title={risk.title}
          description={risk.description}
          severity={risk.severity}
          flaggedByName={risk.flaggedByName}
          flaggedAt={risk.flaggedAt}
          readOnly={readOnly && !sessionAddedIds.has(risk.id)}
          onRemoved={onRiskRemoved}
        />
      ))}
      {addedRisks.map((addedRisk) => (
        <AddedRiskCard
          key={addedRisk.flag.id}
          id={addedRisk.flag.id}
          videoId={videoId}
          title={addedRisk.title}
          description={addedRisk.description}
          severity={tierToSeverity(addedRisk.flag.riskTier ?? 4)}
          onRemoved={handleRemoved}
        />
      ))}

      {open && (
        <div
          ref={formRef}
          className={cx(
            "bg-white rounded-md p-3 flex flex-col gap-3",
            border === "default" && "border border-border-primary",
            guard.nudged && "ring-2 ring-brand",
          )}
        >
          {view === "new" && (
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-1.5">
                <PlusCircle className="w-[18px] h-[18px] text-primary" />
                <span className="text-sm font-medium text-primary">Add a missed risk</span>
              </div>
              <Button
                color="secondary"
                size="sm"
                aria-label="Cancel add risk"
                iconLeading={<Trash02 className="w-4 h-4" />}
                onClick={discard}
              />
            </div>
          )}
          <Input
            size="sm"
            label="What did the AI miss?"
            placeholder="e.g. there's a dog in the background"
            value={description}
            onChange={setDescription}
          />
          <RiskSelect
            label="Risk that was missed"
            groups={riskOptionGroups}
            value={riskKey}
            onChange={(key) => {
              setRiskKey(key);
              setPickedByHand(true);
            }}
            getIndicator={(item) =>
              item.key === OTHER_OPTION_ID ? undefined : <MonitoringDot monitored={isMonitored(item.key)} />
            }
            leadingAdornment={
              selectedItem && <MonitoringDotWithTooltip riskName={selectedItem.name} monitored={selectedMonitored} />
            }
          />
          {selectedItem && !selectedMonitored && (
            <RiskMonitoringTip
              riskId={selectedItem.key}
              riskName={selectedItem.name}
              monitored={false}
              onOpenConfigure={() => setSwitchingOn(selectedItem.key)}
            />
          )}
          {(guard.nudged || justSwitchedOn) && (
            <div className="flex gap-2 rounded-md bg-warning-50 px-3 py-2 text-xs text-warning-700">
              <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
              <div className="flex flex-col">
                <span className="font-semibold">Click Add risk to save.</span>
                {justSwitchedOn && (
                  <span>
                    {selectedItem.name} will be checked in every video processed from now. Videos already processed
                    won't be flagged for it.
                  </span>
                )}
              </div>
            </div>
          )}
          <Button
            color="primary"
            size="sm"
            className="w-full"
            iconLeading={<Plus className="w-4 h-4" />}
            isDisabled={!canSubmit}
            isLoading={addMissedRisk.isPending}
            onClick={handleAdd}
          >
            Add risk
          </Button>
        </div>
      )}

      <LeaveUnfinishedRiskDialog
        open={guard.pendingLeave}
        onKeepAdding={guard.stay}
        onDiscard={() => {
          discard();
          guard.leave();
        }}
      />
    </div>
  );
};
